<template>
  <div
    class="app-selector d-flex flex-column h-100 mt-3"
  >
    <div class="d-flex justify-content-center align-items-center">
      <b-img
        :src="logo"
        loading="lazy"
        class="logo px-3"
      />
    </div>

    <div class="search w-100 mx-auto mt-3 px-5">
      <div class="flex-grow-1 mt-1">
        <c-input-search
          v-model.trim="query"
          data-v-onboarding="app-list"
          :aria-label="$t('search')"
          :placeholder="$t('search')"
          :debounce="200"
        />
      </div>
    </div>

    <div
      class="flex-fill overflow-auto bottom_space"
    >
      <b-container
        class="h-100"
      >
        <draggable
          v-if="filteredApps.length"
          v-model="appList"
          :disabled="!canCreateApplication || query || isMobileResolution"
          group="apps"
          class="h-100 w-100"
          @end="onDrop"
        >
          <transition-group
            name="apps"
            tag="b-row"
            class="d-flex flex-wrap align-items-stretch justify-content-center mx-2"
          >
           <b-col
              v-for="app in filteredApps"
              :key="app.applicationID"
              cols="12"
              sm="6"
              md="4"
              lg="3"
              class="equal-spacing" 
              :data-v-onboarding="getStepName(app.unify.url)"
            >
             <b-card
              no-body
              overlay
              class="app h-100 d-flex flex-column"
              @mouseover="hovered = app.applicationID"
              @mouseleave="hovered = undefined"
            >
              <div class="d-flex flex-grow-1 align-items-center justify-content-center">
                <b-card-img
                  class="rounded-bottom thumbnail"
                  :src="logoUrl(app)"
                  :alt="app.unify.name || app.name"
                  loading="lazy"
                />
              </div>
              <b-card-footer class="app-footer">
                <b-link
                  :href="app.unify.url"
                  :target="openAppInNewTab(app.unify)"
                  :disabled="!app.enabled"
                  class="app-footer-link"
                  v-b-tooltip.hover.top="{
                  title: (app.unify.name || app.name).length > 25 ? (app.unify.name || app.name) : ''
                  }"
                >
                  {{ truncatedAppName(app) }}
                </b-link>
              </b-card-footer>
              <b-link
                :data-test-id="app.name"
                :disabled="!app.enabled"
                :href="app.unify.url"
                :target="openAppInNewTab(app.unify)"
                :style="[{ cursor: `${app.enabled ? 'pointer': canCreateApplication ? 'grab' : 'default'}` }]"
                class="stretched-link"
              />
            </b-card>
          </b-col>
        </transition-group>
      </draggable>
        <div
          v-else
          class="d-flex justify-content-center w-100"
        >
          <h4
            data-test-id="heading-no-apps"
            class="mt-5"
          >
            {{ $t('no-applications') }}
          </h4>
        </div>
      </b-container>
    </div>

    <portal
      to="topbar-help-dropdown"
    >
      <b-dropdown-item
        data-test-id="dropdown-helper-tour"
        @click="$refs.tour.onStartClick()"
      >
        {{ $t('start-tour') }}
      </b-dropdown-item>
    </portal>

    <tour-start
      @start="startTour"
    />

    <tour
      ref="tour"
      name="app-list"
      :steps="filteredSteps"
    />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import Draggable from 'vuedraggable'
import { components, url } from '@cortezaproject/corteza-vue'
const { Tour, TourStart, CInputSearch } = components

export default {
  i18nOptions: {
    namespaces: 'layout',
  },

  components: {
    Draggable,
    Tour,
    TourStart,
    CInputSearch,
  },

  props: {
    logo: {
      type: String,
      default: () => '',
    },
  },

  data () {
    return {
      query: '',

      appList: [],

      canCreateApplication: false,
      canPin: false,

      hovered: undefined,

      isMobileResolution: false,

      steps: [
        { name: 'app-list', dynamic: false },
        { name: 'low-code', dynamic: true },
        { name: 'crm', dynamic: true },
        { name: 'reporter', dynamic: true },
        { name: 'workflow', dynamic: true },
        { name: 'profile', dynamic: false },
      ],

    }
  },

  computed: {
    ...mapGetters({
      apps: 'applications/unifyOnly',
    }),

    filteredApps () {
      const query = (this.query || '').toUpperCase()
      return this.query
        ? this.appList.filter(({ name }) => (name.toUpperCase()).includes(query))
        : this.appList
    },

    filteredSteps () {
      return this.steps.filter(step => {
        if (step.dynamic) {
          return this.filteredApps.some(app => {
            return this.getStepName(app.unify.url) === step.name
          })
        }
        return true
      }).map(s => { return s.name })
    },
  },

  watch: {
    'apps': {
      immediate: true,
      handler (apps) {
        this.appList = apps
      },
    },
  },

  created () {
    this.fetchEffective()
    if (window.innerWidth < 576) {
      this.isMobileResolution = true
    }
  },

  methods: {
    ...mapActions({
      reorderApp: 'applications/reorder',
      pinApp: 'applications/pin',
      unpinApp: 'applications/unpin',
    }),
  truncatedAppName (app) {
    const name = app.unify.name || app.name || ''
    return name.length > 25 ? name.slice(0, 25) + '...' : name
  },
    getStepName (url) {
      switch (url) {
        case 'compose/':
          return 'low-code'
        case 'compose/ns/crm/pages':
          return 'crm'
        case 'reporter/':
          return 'reporter'
        case 'workflow/':
          return 'workflow'
      }
    },

    fetchEffective () {
      this.$SystemAPI.permissionsEffective({ resource: 'application' })
        .then(p => {
          this.canCreateApplication = p.find(per => per.operation === 'application.create').allow || false
          // this.canPin = p.find(({ resource, operation, allow }) => resource === 'system' && operation === 'application.flag.self').allow
        })
    },

    handlePin (pin = true, applicationID) {
      if (pin) {
        this.unpinApp({ applicationID, ownedBy: this.$auth.user.userID })
      } else {
        this.pinApp({ applicationID, ownedBy: this.$auth.user.userID })
      }
    },

    async onDrop () {
      const applicationIDs = this.appList.map(({ applicationID }) => applicationID)
      await this.reorderApp(applicationIDs)
    },

    startTour () {
      this.$refs.tour.onStart()
    },

    logoUrl (app) {
      if (!app.unify.logo) {
        return 'applications/default-app.png'
      }

      const apiSystem = '/api/system'
      const apiBaseUrl = (new URL(url.Make({ url: this.$SystemAPI.baseURL }))).toString()

      // Properly handle uploaded logos
      // but cut away only /api/system (without any potential base-url prefix)
      if (app.unify.logo.startsWith(apiSystem)) {
        // remove path from the URL
        return apiBaseUrl.substring(0, apiBaseUrl.length - apiSystem.length) + app.unify.logo
      }

      // Provisioned app logos
      return app.unify.logo
    },

    openAppInNewTab (unify) {
      if(unify.url.includes('jitsi') || unify.new_tab){
        return "_blank"
      }else{
        return ""
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.bottom_space {
  margin-bottom: 2rem;
  margin-top: 1rem;
}
.equal-spacing {
  padding: 0.75rem !important;
}
.app-selector {
  .logo {
    max-height: 20vh;
    max-width: 400px;
    width: auto;
  }

  @media only screen and (max-width: 576px) {
    .logo {
     max-width: 150px;
      max-height: 150px;
      object-fit: contain;
    }
    .bottom_space {
    margin-bottom: 2rem;
}
  }

  .search {
    max-width: 600px;
  }

 .app {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  border-radius: 7px;
  transition: all 0.3s ease;
  border: 1px solid var(--extra-light);
  box-shadow: 0 2px 6px rgba(0,0,0,0.25);
  height: 100%;
  min-height: 13rem;
  position: relative;

    &:hover {
      transform: translateY(-6px);
      box-shadow: 0 8px 16px rgba(0,0,0,0.4);
    }

    /* Center image wrapper */
    .thumbnail {
      max-width: 100%;
      max-height: 150px;
      object-fit: contain;
    }

    /* body flex to center image */
    ::v-deep(.card-body) {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 1rem;
      position: relative;
    }

    /* Footer sticks to bottom */
    .app-footer {
      margin-top: auto;
    }
  }

  /* stretched link styling */
  .stretched-link {
    z-index: 1;
  }

  /* text links inside app card */
  .app-title,
  .app-subtitle,
  .app-desc {
    text-decoration: none !important;
    color: inherit !important;
    cursor: pointer;
    position: relative;
    z-index: 2;
  }

  /* Disabled state */
  .disabled {
    opacity: 0.5;
    pointer-events: none;
  }
  .star {
    position: absolute;
    top: .2rem;
    left: .2rem;
    padding: 0;
    margin: 0;
    background-color: transparent;
    border: none;

    .star-icon {
      fill: var(--warning);
      width: 1.2rem;
      height: 1.2rem;
    }
  }

  /* transition animations */
  .apps-leave-active {
    position: absolute;
    transition: opacity 0.25s ease;
  }
  .apps-enter,
  .apps-leave-to {
    opacity: 0;
  }
  .apps-move {
    transition: transform 0.25s ease;
  }
}

.app-footer {
  background: var(--primary);
  color: var(--white);
  font-size: 0.8rem;
  font-weight: 500;
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
  padding: 0.5rem 1rem;
  margin-top: auto;
  width: 100%;
  text-align: center;
  position: relative;
  z-index: 2;
}

.app-footer-link {
  text-decoration: none !important;
  color: var(--white) !important;
  cursor: pointer;
  position: relative;
  z-index: 2;
}
</style>
